import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, TextField, InputAdornment, FormControl, InputLabel,
  Select, MenuItem, Tooltip, IconButton, Chip, Grid, Card, CardContent,
  LinearProgress, Divider, useTheme, alpha
} from '@mui/material';
import {
  Search, Visibility, Inventory2, Category,
  BuildCircle, Warehouse, TrendingUp, PieChart
} from '@mui/icons-material';
import { inventoryAPI, unitProductsAPI } from '../services/api';
import DataTable from '../components/common/DataTable';
import { toast } from 'react-toastify';
import UnitDetailDialog from '../components/inventory/UnitDetailDialog';

const UnitTrackerPage = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const urlSearch = searchParams.get('search') || '';
  const urlModel = searchParams.get('model') || '';
  const urlWarehouseId = searchParams.get('warehouse_id') || '';
  
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState(urlSearch);
  // Trim the initial model filter from URL
  const [modelFilter, setModelFilter] = useState(urlModel.trim());
  const [statusFilter, setStatusFilter] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState(urlWarehouseId);
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  const [selectedUnitRow, setSelectedUnitRow] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [usingInventoryFallback, setUsingInventoryFallback] = useState(false);
  
  // State for dashboard statistics
  const [stats, setStats] = useState({
    totalUnits: 0,
    byProduct: {},
    byCondition: {},
    byStatus: {},
    byWarehouse: {}
  });

  // ─── helper: condition colour ───────────────────────────────────
  const conditionColor = (c) => ({
    new: 'success',
    used: 'warning', 
    certified_used: 'info',
    damaged: 'error', 
    refurbished: 'secondary',
    pending_detail: 'warning',
  }[c] || 'default');

  // ─── helper: unit status colour ───────────────────────────────────
  const unitStatusColor = (s) => ({
    in_stock: 'success',
    reserved: 'warning', 
    sold: 'default',
    returned: 'info',
    transferred: 'secondary',
    written_off: 'error',
    untracked: 'warning',
  }[s] || 'default');

  // ─── helper: get theme color for progress bars ───────────────────────
  const getThemeColor = (colorName) => {
    const colorMap = {
      success: theme.palette.success.main,
      warning: theme.palette.warning.main,
      info: theme.palette.info.main,
      error: theme.palette.error.main,
      secondary: theme.palette.secondary.main,
      default: theme.palette.grey[500],
    };
    return colorMap[colorName] || theme.palette.grey[500];
  };

  // ─── helper: get status icon ───────────────────────────────────
  const getStatusIcon = (status) => {
    const icons = {
      in_stock: '✓',
      reserved: '🔒',
      sold: '💰',
      returned: '↩️',
      transferred: '🚚',
      written_off: '⚠️'
    };
    return icons[status] || '📦';
  };

  // Calculate statistics from units data
  const calculateStats = useCallback((unitsData) => {
    const newStats = {
      totalUnits: 0,
      byProduct: {},
      byCondition: {},
      byStatus: {},
      byWarehouse: {}
    };

    unitsData.forEach(unit => {
      const count = unit.is_untracked ? (parseInt(unit.quantity, 10) || 0) : 1;
      newStats.totalUnits += count;

      // Count by product
      const productName = unit.product_name || 'Unknown';
      newStats.byProduct[productName] = (newStats.byProduct[productName] || 0) + count;

      // Count by condition
      const condition = unit.is_untracked ? 'pending_detail' : (unit.condition || 'unknown');
      newStats.byCondition[condition] = (newStats.byCondition[condition] || 0) + count;

      // Count by status
      const status = unit.is_untracked ? 'untracked' : (unit.status || 'unknown');
      newStats.byStatus[status] = (newStats.byStatus[status] || 0) + count;

      // Count by warehouse
      const warehouse = unit.warehouse_name || 'Unknown';
      newStats.byWarehouse[warehouse] = (newStats.byWarehouse[warehouse] || 0) + count;
    });

    return newStats;
  }, []);

// Inside fetchUnits
const fetchUnits = useCallback(async () => {
  try {
    setLoading(true);
    const params = {
      page: page + 1,
      limit: rowsPerPage,
      ...(search && { search }),
      ...(modelFilter?.trim() && { model: modelFilter.trim() }),
      ...(statusFilter && { status: statusFilter }),
      ...(warehouseFilter && { warehouse_id: warehouseFilter }),
    };
    const { data } = await unitProductsAPI.getAll(params);

    const trackedUnits = data.data || [];
    if (trackedUnits.length > 0) {
      setUsingInventoryFallback(false);
      setUnits(trackedUnits);
      setTotal(data.pagination?.total || 0);
      setStats(calculateStats(trackedUnits));
      return;
    }

    const inventoryResponse = await inventoryAPI.getAll({
      page: 1,
      limit: 1000,
      ...(warehouseFilter && { warehouse_id: warehouseFilter }),
    });

    const inventoryRows = (inventoryResponse.data.data || [])
      .filter((row) => {
        const haystack = [
          row.name,
          row.product_name,
          row.model,
          row.sku,
          row.warehouse_name,
        ].filter(Boolean).join(' ').toLowerCase();

        const matchesSearch = search ? haystack.includes(search.toLowerCase()) : true;
        const matchesModel = modelFilter
          ? (row.model || '').toLowerCase().includes(modelFilter.toLowerCase())
          : true;
        const matchesStatus = statusFilter
          ? statusFilter.toLowerCase() === 'untracked'
          : true;

        return matchesSearch && matchesModel && matchesStatus && Number(row.quantity) > 0;
      })
      .map((row) => ({
        id: `untracked-${row.product_id}-${row.warehouse_id}`,
        sku: row.sku,
        product_name: row.product_name || row.name,
        brand_name: row.brand_name || '',
        model: row.model,
        chassis_number: '',
        condition: '',
        status: 'untracked',
        warehouse_name: row.warehouse_name,
        received_date: row.updated_at || row.created_at,
        quantity: row.quantity,
        product_id: row.product_id,
        warehouse_id: row.warehouse_id,
        is_untracked: true,
      }));

    setUsingInventoryFallback(inventoryRows.length > 0);
    setUnits(inventoryRows);
    setTotal(inventoryRows.length);
    setStats(calculateStats(inventoryRows));
  } catch {
    toast.error('Failed to load units');
  } finally {
    setLoading(false);
  }
}, [page, rowsPerPage, search, modelFilter, statusFilter, warehouseFilter, calculateStats]);

  const fetchWarehouses = useCallback(async () => {
    try {
      const { data } = await inventoryAPI.getWarehouses();
      setWarehouses(data.data || []);
    } catch {
      toast.error('Failed to load warehouses');
    }
  }, []);

  // FIX: Improved fetchAvailableModels to trim model names and remove duplicates properly
const fetchAvailableModels = useCallback(async () => {
  try {
    const [unitResponse, inventoryResponse] = await Promise.all([
      unitProductsAPI.getAll({ page: 1, limit: 10000 }),
      inventoryAPI.getAll({ page: 1, limit: 1000 }),
    ]);

    const unitModels = unitResponse.data.data?.map((unit) => unit.model?.trim()).filter(Boolean) || [];
    const inventoryModels = inventoryResponse.data.data?.map((row) => row.model?.trim()).filter(Boolean) || [];
    const models = [...new Set([...unitModels, ...inventoryModels])];
    models.sort();
    setAvailableModels(models);
  } catch {
    toast.error('Failed to load available models');
  }
}, []);

  useEffect(() => { 
    fetchUnits(); 
    fetchWarehouses();
    fetchAvailableModels();
    
    // Listen for inventory updates from other components
    const handleInventoryUpdate = () => {
      fetchUnits();
      fetchAvailableModels(); // Also refresh the models list when inventory updates
    };
    
    window.addEventListener('inventory-updated', handleInventoryUpdate);
    
    return () => {
      window.removeEventListener('inventory-updated', handleInventoryUpdate);
    };
  }, [fetchUnits, fetchWarehouses, fetchAvailableModels]);

  // FIX: Add useEffect to sync URL params with state and trim model value
  useEffect(() => {
    if (urlModel && urlModel.trim() !== modelFilter) {
      setModelFilter(urlModel.trim());
    }
    if (urlSearch && urlSearch !== search) {
      setSearch(urlSearch);
    }
    if (urlWarehouseId && urlWarehouseId !== warehouseFilter) {
      setWarehouseFilter(urlWarehouseId);
    }
  }, [urlModel, urlSearch, urlWarehouseId, modelFilter, search, warehouseFilter]);

  const openDetail = (unit) => {
    setSelectedUnitId(unit.id);
    setSelectedUnitRow(unit);
    setDetailOpen(true);
  };

  // FIX: Handle model selection - trim the value when setting
  const handleModelChange = (value) => {
    const trimmedValue = value.trim();
    setModelFilter(trimmedValue);
    setPage(0);
  };

  const columns = [
    { key: 'sku', label: 'SKU',
      render: v => <Typography fontFamily="monospace" fontSize="0.75rem" fontWeight={600}>{v}</Typography> },
    { key: 'product_name', label: 'Product',
      render: (v, row) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{v}</Typography>
          <Typography variant="caption" color="text.secondary">
            {row.is_untracked
              ? `${row.quantity} in stock, unit details missing`
              : (row.brand_name || row.model || 'Tracked unit')}
          </Typography>
        </Box>
      )},
    { key: 'model', label: 'Model',
      render: v => v
        ? <Typography fontFamily="monospace" fontSize="0.8rem">{v.trim()}</Typography>
        : <Typography color="text.disabled" fontSize="0.8rem">—</Typography> },
    { key: 'chassis_number', label: 'Chassis / VIN',
      render: (v, row) => v
        ? <Typography fontFamily="monospace" fontSize="0.8rem">{v}</Typography>
        : <Typography color="text.disabled" fontSize="0.8rem">—</Typography> },
    { key: 'condition', label: 'Condition',
      render: (v, row) => {
        const label = row.is_untracked ? 'needs details' : (v || 'new');
        const color = row.is_untracked ? conditionColor('pending_detail') : conditionColor(v);
        return <Chip label={label} size="small" color={color} />;
      } },
    { key: 'status', label: 'Status',
      render: (v, row) => {
        const label = row.is_untracked ? 'inventory only' : (v?.replace('_', ' ') || '?');
        const color = row.is_untracked ? unitStatusColor('untracked') : unitStatusColor(v);
        return <Chip label={label} size="small" color={color} />;
      } },
    { key: 'warehouse_name', label: 'Warehouse',
      render: (v, row) => {
        if (row.transferred_from_warehouse_name) {
          return (
            <Box>
              <Typography variant="body2">{v}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Transferred from {row.transferred_from_warehouse_name}
              </Typography>
            </Box>
          );
        }
        return v || '—';
      }},
    { key: 'received_date', label: 'Received',
      render: (v, row) => {
        const date = v || row.created_at || row.updated_at;
        return date ? new Date(date).toLocaleDateString() : '—';
      }},
    { key: 'id', label: 'Actions', align: 'center',
      render: (_, row) => (
        <Tooltip title={row.is_untracked ? 'View inventory row details' : 'View / Edit unit'}>
          <IconButton size="small" color="primary" onClick={() => openDetail(row)}>
            <Visibility />
          </IconButton>
        </Tooltip>
      )},
  ];

  // Prepare top products for display
  const topProducts = Object.entries(stats.byProduct)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
            Unit Tracker
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {total} tracked unit{total !== 1 ? 's' : ''} • Individual item tracking by chassis, model, warehouse, and condition
          </Typography>
        </Box>
        {/* <Button
          variant="contained"
          startIcon={<DirectionsCar />}
          onClick={() => {
            setAddUnitOpen(true);
          }}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          Add Unit
        </Button> */}
      </Box>

      {/* Statistics Dashboard */}
      <Box sx={{ mb: 4 }}>
        {/* Total Units Card */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card 
              sx={{ 
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
                borderRadius: 3,
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Inventory2 sx={{ fontSize: 40, opacity: 0.9 }} />
                  <Typography variant="h3" fontWeight={800}>
                    {stats.totalUnits}
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  Total Units
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                  Across all warehouses
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={9}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUp sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight={600}>
                    Quick Overview
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Conditions</Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {Object.keys(stats.byCondition).length}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Status Types</Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {Object.keys(stats.byStatus).length}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Products</Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {Object.keys(stats.byProduct).length}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Warehouses</Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {Object.keys(stats.byWarehouse).length}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Detailed Statistics */}
        <Grid container spacing={3}>
          {/* By Status */}
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ borderRadius: 3, height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PieChart sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="h6" fontWeight={600}>
                    By Status
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {Object.entries(stats.byStatus).map(([status, count]) => {
                    const percentage = (count / stats.totalUnits * 100).toFixed(1);
                    return (
                      <Box key={status} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '1.2rem' }}>
                              {getStatusIcon(status)}
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {status.replace('_', ' ')}
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={600} color="primary">
                            {count} ({percentage}%)
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={parseFloat(percentage)} 
                          sx={{ 
                            height: 6, 
                            borderRadius: 3,
                            backgroundColor: alpha(getThemeColor(status), 0.1),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getThemeColor(status),
                              borderRadius: 3,
                            }
                          }}
                        />
                      </Box>
                    );
                  })}
                  {Object.keys(stats.byStatus).length === 0 && (
                    <Typography variant="body2" color="text.secondary" align="center">
                      No data available
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* By Condition */}
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ borderRadius: 3, height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BuildCircle sx={{ mr: 1, color: 'warning.main' }} />
                  <Typography variant="h6" fontWeight={600}>
                    By Condition
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {Object.entries(stats.byCondition).map(([condition, count]) => {
                    const percentage = (count / stats.totalUnits * 100).toFixed(1);
                    return (
                      <Box key={condition} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" fontWeight={500} textTransform="capitalize">
                            {condition}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} color="primary">
                            {count} ({percentage}%)
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={parseFloat(percentage)} 
                          sx={{ 
                            height: 6, 
                            borderRadius: 3,
                            backgroundColor: alpha(getThemeColor(condition), 0.1),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getThemeColor(condition),
                              borderRadius: 3,
                            }
                          }}
                        />
                      </Box>
                    );
                  })}
                  {Object.keys(stats.byCondition).length === 0 && (
                    <Typography variant="body2" color="text.secondary" align="center">
                      No data available
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* By Warehouse */}
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ borderRadius: 3, height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Warehouse sx={{ mr: 1, color: 'secondary.main' }} />
                  <Typography variant="h6" fontWeight={600}>
                    By Warehouse
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {Object.entries(stats.byWarehouse).map(([warehouse, count]) => {
                    const percentage = (count / stats.totalUnits * 100).toFixed(1);
                    return (
                      <Box key={warehouse} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 150 }}>
                            {warehouse}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} color="primary">
                            {count} ({percentage}%)
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={parseFloat(percentage)} 
                          sx={{ 
                            height: 6, 
                            borderRadius: 3,
                            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: theme.palette.secondary.main,
                              borderRadius: 3,
                            }
                          }}
                        />
                      </Box>
                    );
                  })}
                  {Object.keys(stats.byWarehouse).length === 0 && (
                    <Typography variant="body2" color="text.secondary" align="center">
                      No data available
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Top Products */}
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ borderRadius: 3, height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Category sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="h6" fontWeight={600}>
                    Top Products
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {topProducts.map(([product, count], index) => {
                    const percentage = (count / stats.totalUnits * 100).toFixed(1);
                    return (
                      <Box key={product} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={700} color="primary">
                              #{index + 1}
                            </Typography>
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 120 }}>
                              {product}
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={600} color="primary">
                            {count} ({percentage}%)
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={parseFloat(percentage)} 
                          sx={{ 
                            height: 6, 
                            borderRadius: 3,
                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: index === 0 ? theme.palette.warning.main : theme.palette.success.main,
                              borderRadius: 3,
                            }
                          }}
                        />
                      </Box>
                    );
                  })}
                  {topProducts.length === 0 && (
                    <Typography variant="body2" color="text.secondary" align="center">
                      No data available
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search chassis, model, product, color, notes..." size="small" sx={{ minWidth: 300 }}
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
        {/* FIX: Model filter select with trimmed values */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Model</InputLabel>
          <Select 
            value={modelFilter || ""} 
            label="Model"
            onChange={e => handleModelChange(e.target.value)}
          >
            <MenuItem value="">All Models</MenuItem>
            {availableModels.map(model => (
              <MenuItem key={model} value={model}>{model}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status"
            onChange={e => { setStatusFilter(e.target.value); setPage(0); }}>
            <MenuItem value="">All Statuses</MenuItem>
            {['in_stock','reserved','sold','returned','transferred','written_off','untracked'].map(s =>
              <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Warehouse</InputLabel>
          <Select value={warehouseFilter} label="Warehouse"
            onChange={e => { setWarehouseFilter(e.target.value); setPage(0); }}>
            <MenuItem value="">All Warehouses</MenuItem>
            {warehouses.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {usingInventoryFallback && (
        <Box
          sx={{
            mb: 3,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
            backgroundColor: alpha(theme.palette.warning.main, 0.08),
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Stock exists for these product models in inventory, but the unique unit record has not been captured yet.
            These rows are shown from inventory until chassis-level details are added.
          </Typography>
        </Box>
      )}

      <DataTable
        columns={columns} rows={units} loading={loading} total={total}
        page={page} rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        emptyMessage="No tracked units found — add stock with unit details to start tracking"
      />

      <UnitDetailDialog
        open={detailOpen}
        unitId={selectedUnitId}
        fallbackUnit={selectedUnitRow}
        onClose={() => {
          setDetailOpen(false);
          setSelectedUnitId(null);
          setSelectedUnitRow(null);
        }}
        onUpdated={fetchUnits}
      />
    </Box>
  );
};

export default UnitTrackerPage;
